// IMPORTANT IMPLEMENTATION NOTE:
// This CalendarTable component uses a universal approach to support all festival time configurations.
//
// UNIVERSAL ALGORITHM:
// The component dynamically generates time slots and positioning based on database schedule parameters:
//   * scheduleStartHour: The hour when the calendar grid begins (e.g., 12 for 12PM, 17 for 5PM)
//   * scheduleEndHour: The hour when the calendar grid ends (e.g., 22 for 10PM, 24 for 12AM, 6 for 6AM)
//   * scheduleIsOvernight: Whether the schedule crosses midnight (true for overnight festivals)
//
// KEY FEATURES:
// - Time slot count and labels are dynamically generated based on schedule parameters
// - Set positioning uses a universal formula that works with any hour range
// - Inverse order positioning calculates from the end of the schedule
// - Stage column widths are dynamic based on the number of stages
// - Handles both overnight and day festivals seamlessly
//
// EXAMPLES:
// - Day Festival (ASOT): scheduleStartHour = 12, scheduleEndHour = 22, scheduleIsOvernight = false
// - Overnight Festival (EDC): scheduleStartHour = 17, scheduleEndHour = 6, scheduleIsOvernight = true
// - Midnight End (Ultra): scheduleStartHour = 12, scheduleEndHour = 24, scheduleIsOvernight = false
// - Long Festival (Starbase): scheduleStartHour = 11, scheduleEndHour = 7, scheduleIsOvernight = true
//
// The algorithm automatically handles:
// - schedule_end_hour = 24 (midnight, not overnight)
// - schedule_end_hour = 0-6 with overnight = true (early morning ends)
// - Any combination of start/end times within 24-hour periods

"use client";

import { RiAppleFill, RiLiveLine, RiSoundcloudFill } from "@remixicon/react";
import { formatCompactTimeWithPreferences, getCurrentTimePosition, isDayToday } from "@workspace/shared-utils";
import type { FestivalDetails } from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import {
	type CSSProperties,
	memo,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { getStageColumnWidth } from "@/lib/features/festival/calendar/calendar-dimensions";

// import {
//   useCalendarConfig,
//   useScrollLeft,
//   useSetScrollLeft
// } from "@/stores/calendar-state"

export interface SetData {
	id: string;
	time: string;
	artist: string;
	stage: string;
	startHour: number; // Decimal hour value (e.g. 12.5 = 12:30)
	duration: number; // Duration in hours (e.g. 1.5 = 1h 30m)
	youtube_url?: string | null; // YouTube URL for stream of this set
	soundcloud_url?: string | null; // SoundCloud URL for recording of this set
	apple_music_url?: string | null; // Apple Music URL for recording of this set
	status?: {
		isInSchedule: boolean;
		sawLive: boolean;
		sawStream: boolean;
		mustSee: boolean;
	};
}

// Helper to determine which recording icon to show based on available URLs
const getRecordingIcon = (set: SetData) => {
	const hasYoutube = !!set.youtube_url;
	const hasSoundcloud = !!set.soundcloud_url;
	const hasAppleMusic = !!set.apple_music_url;

	const sourceCount = [hasYoutube, hasSoundcloud, hasAppleMusic].filter(Boolean).length;

	if (sourceCount > 1 || hasYoutube) {
		return RiLiveLine;
	}
	if (hasSoundcloud) {
		return RiSoundcloudFill;
	}
	if (hasAppleMusic) {
		return RiAppleFill;
	}
	return null;
};

interface CalendarTableProps {
	data: SetData[];
	className?: string;
	onToggleSet?: (setId: string) => void;
	onStartReview?: (setId: string) => void;
	renderItem?: (
		set: SetData,
		statusInfo: { icon: string; text: string },
		style: CSSProperties,
	) => ReactNode;
	inverseOrder?: boolean; // When true, displays sets in reverse chronological order (latest at top)
	scheduleStartHour?: number; // The starting hour of the calendar grid (24h format, e.g., 12 for 12 PM)
	scheduleEndHour?: number; // The ending hour of the calendar grid (24h format, e.g., 1 for 1 AM next day)
	scheduleIsOvernight?: boolean; // Whether the schedule crosses midnight
	allStageNames?: string[]; // Optional list of all stage names for consistent column ordering
	readOnly?: boolean; // When true, disables interactive elements and shows view-only mode
	hourHeight?: number;
	minSetHeight?: number;
	onCellClick?: (stageName: string, hour: number) => void; // Business dashboard: click empty cell to add set
	contentClassName?: string; // Extra classes for the scrollable content area (e.g., overflow-visible during drag)
	selectedDay?: string; // Current selected day name (e.g., "friday") for current time indicator
	festivalDetails?: FestivalDetails | null; // Festival details for date comparison
}

export const CalendarTable = memo(function CalendarTable({
	data,
	className,
	onToggleSet,
	onStartReview,
	renderItem,
	inverseOrder = false,
	scheduleStartHour = 12, // Default: 12 PM (common start time)
	scheduleEndHour = 24, // Default: 12 AM midnight (common end time)
	scheduleIsOvernight = false, // Default: false (most festivals end at midnight)
	allStageNames = [],
	readOnly = false,
	hourHeight = 160,
	minSetHeight = 80,
	onCellClick,
	contentClassName,
	selectedDay,
	festivalDetails,
}: CalendarTableProps) {
	// Get user preferences for time formatting
	const userPreferences = useUserPreferences();
	// Note: Filtering functionality removed as it's not currently used
	// Can be re-enabled in the future if needed for showing only reviewed sets
	const filteredData = data;

	// Group sets by stage
	const stages = useMemo(() => {
		const stageMap = new Map<string, SetData[]>();

		// First add all known stages with empty arrays to ensure they're all included
		allStageNames.forEach((stageName) => {
			if (!stageMap.has(stageName)) {
				stageMap.set(stageName, []);
			}
		});

		// Then add the filtered data
		filteredData.forEach((set) => {
			if (!stageMap.has(set.stage)) {
				stageMap.set(set.stage, []);
			}
			stageMap.get(set.stage)?.push(set);
		});

		stageMap.forEach((sets) => {
			if (inverseOrder) {
				sets.sort((a, b) => {
					const aEndHour = a.startHour + a.duration;
					const bEndHour = b.startHour + b.duration;
					return bEndHour - aEndHour;
				});
			} else {
				sets.sort((a, b) => a.startHour - b.startHour);
			}
		});

		// Use allStageNames to order the stages in the same order they were passed in
		// This ensures the stage order in the calendar matches the desired custom order
		if (allStageNames.length > 0) {
			return allStageNames
				.filter((stageName) => stageMap.has(stageName))
				.map(
					(stageName) =>
						[stageName, stageMap.get(stageName) || []] as [string, SetData[]],
				);
		}

		// Fallback to default behavior if no allStageNames are provided
		return Array.from(stageMap.entries());
	}, [filteredData, inverseOrder, allStageNames]);

	// Calculate dynamic stage column width based on number of stages
	const stageColumnWidth = useMemo(() => {
		return getStageColumnWidth(stages.length);
	}, [stages.length]);

	// Calculate total hours the schedule visually spans
	const totalHoursInSchedule = useMemo(() => {
		// Special case: Full 24-hour day (midnight to midnight)
		if (scheduleStartHour === 0 && scheduleEndHour === 24) {
			return 24;
		}

		// Handle midnight as end hour (24 = midnight same day, not overnight)
		// When scheduleEndHour is 24, it means midnight of the same day
		if (scheduleEndHour === 24 && !scheduleIsOvernight) {
			return 24 - scheduleStartHour;
		}

		// For overnight festivals or when end hour is 0-23
		const actualEndHour = scheduleEndHour === 24 ? 0 : scheduleEndHour;
		const isActuallyOvernight =
			scheduleIsOvernight && actualEndHour < scheduleStartHour;

		// Universal calculation for all festivals
		if (isActuallyOvernight) {
			return 24 - scheduleStartHour + actualEndHour;
		} else {
			return Math.abs(actualEndHour - scheduleStartHour);
		}
	}, [scheduleStartHour, scheduleEndHour, scheduleIsOvernight]);

	const timeSlots = useMemo(() => {
		// Universal time slot generation for all festivals
		// Dynamically generate time slots based on schedule parameters
		const numberOfLabels = totalHoursInSchedule + 1;
		const slots = Array.from({ length: numberOfLabels }, (_, i) => {
			const currentDisplayHour = (scheduleStartHour + i) % 24;

			// Use compact preference-aware formatting for time labels (e.g., "9PM" or "21")
			const date = new Date();
			date.setHours(currentDisplayHour, 0, 0, 0);
			const hourString = formatCompactTimeWithPreferences(
				date,
				userPreferences,
			);

			return {
				id: `time-slot-${i}`,
				hour: currentDisplayHour, // Actual 24h hour value for mapping
				label: hourString,
			};
		});

		return inverseOrder ? [...slots].reverse() : slots;
	}, [totalHoursInSchedule, scheduleStartHour, inverseOrder, userPreferences]);

	const HOUR_HEIGHT = hourHeight; // px
	const topOffset = inverseOrder ? 16 : 0;

	// Current time indicator
	const [currentTimeOffset, setCurrentTimeOffset] = useState<number | null>(
		() => {
			if (!selectedDay || !isDayToday(selectedDay, festivalDetails)) return null;
			return getCurrentTimePosition(
				scheduleStartHour,
				scheduleEndHour,
				scheduleIsOvernight,
				festivalDetails?.schedule_day_start_hour,
				festivalDetails?.timezone,
			);
		},
	);

	useEffect(() => {
		if (!selectedDay || !isDayToday(selectedDay, festivalDetails)) {
			setCurrentTimeOffset(null);
			return;
		}

		const update = () => {
			setCurrentTimeOffset(
				getCurrentTimePosition(
					scheduleStartHour,
					scheduleEndHour,
					scheduleIsOvernight,
					festivalDetails?.schedule_day_start_hour,
					festivalDetails?.timezone,
				),
			);
		};

		update();
		const interval = setInterval(update, 60_000);
		return () => clearInterval(interval);
	}, [selectedDay, festivalDetails, scheduleStartHour, scheduleEndHour, scheduleIsOvernight]);

	const currentTimeTop = useMemo(() => {
		if (currentTimeOffset === null) return null;
		if (inverseOrder) {
			return (totalHoursInSchedule - currentTimeOffset) * HOUR_HEIGHT + topOffset;
		}
		return currentTimeOffset * HOUR_HEIGHT;
	}, [currentTimeOffset, inverseOrder, totalHoursInSchedule, HOUR_HEIGHT, topOffset]);

	const getStatusInfo = (status: SetData["status"]) => {
		if (!status) return { icon: "ri-add-line", text: "Add to schedule" };
		if (status.sawStream) return { icon: "ri-live-line", text: "Saw stream" };
		if (status.sawLive) return { icon: "ri-mic-fill", text: "Saw live" };
		if (status.mustSee) return { icon: "ri-star-fill", text: "Must see" };
		if (status.isInSchedule)
			return { icon: "ri-eye-line", text: "Want to see" };
		return { icon: "ri-add-line", text: "Add to schedule" };
	};

	const contentRef = useRef<HTMLDivElement>(null);
	const [scrollLeft, setScrollLeft] = useState(0);

	const handleScroll = useCallback(() => {
		if (contentRef.current) {
			setScrollLeft(contentRef.current.scrollLeft);
		}
	}, []);

	const getSetEvents = (set: SetData) => {
		// For read-only mode, don't attach any events
		if (readOnly) {
			return {};
		}

		// Otherwise, attach the normal events
		return {
			onClick: onToggleSet ? () => onToggleSet(set.id) : undefined,
			onDoubleClick: onStartReview ? () => onStartReview(set.id) : undefined,
		};
	};

	const defaultSetRenderer = (
		set: SetData,
		statusInfo: { icon: string; text: string },
		style: CSSProperties,
	) => {
		return (
			<div
				key={set.id}
				className={cn(
					"absolute flex flex-col overflow-hidden rounded-md border border-border bg-popover p-2 text-popover-foreground text-xs transition-all",
					set.status?.isInSchedule && "border-primary/60 bg-primary/5",
					(set.status?.sawLive || set.status?.sawStream) &&
						"border-blue-500/60 bg-blue-500/5",
					set.status?.mustSee && "border-yellow-500/60 bg-yellow-500/5",
					(onToggleSet || onStartReview) &&
						!readOnly &&
						"cursor-pointer hover:border-primary hover:bg-primary/10 active:bg-primary/20",
				)}
				style={style}
				{...getSetEvents(set)}
			>
				<div className="mb-1 flex items-center justify-between">
					<div className="flex-1 truncate font-medium">{set.artist}</div>
					{!readOnly && (
						<div className="flex items-center text-muted-foreground">
							<i className={`ri ${statusInfo.icon} text-lg`} />
						</div>
					)}
				</div>
				<div className="flex items-center gap-1 text-2xs text-muted-foreground">
					<span>{set.time}</span>
					{(() => {
						const RecordingIcon = getRecordingIcon(set);
						return RecordingIcon ? <RecordingIcon className="h-3 w-3 text-muted-foreground" /> : null;
					})()}
				</div>
			</div>
		);
	};

	return (
		<div className={cn("h-full w-full overflow-auto rounded-2xl", className)}>
			<div
				className={cn(
					"relative rounded-md border bg-[hsl(var(--calendar-bg))]",
					inverseOrder && "calendar-inverse",
				)}
			>
				<div className="grid grid-cols-[40px_1fr]">
					<div className="sticky top-0 left-0 z-[15] flex h-12 items-center justify-between border-r border-b bg-muted px-2 font-semibold text-2xs text-foreground">
						<span />
					</div>
					<div className="sticky top-0 z-10 overflow-hidden bg-muted">
						<div
							className="inline-grid"
							style={{
								gridTemplateColumns: `repeat(${stages.length}, minmax(0, ${stageColumnWidth}px))`,
								minWidth: `${stages.length * stageColumnWidth}px`,
								transform: `translateX(-${scrollLeft}px)`,
							}}
						>
							{stages.map(([stageName]) => (
								<div
									key={stageName}
									className="flex h-12 items-center border-r border-b bg-muted px-4 font-semibold text-foreground text-xs"
								>
									{stageName}
								</div>
							))}
						</div>
					</div>
					<div className="sticky left-0 z-10 border-r bg-muted/50">
						{timeSlots.map((time) => (
							<div
								key={time.id}
								className="flex items-start px-2 pt-2 font-semibold text-2xs text-foreground"
								style={{ height: `${HOUR_HEIGHT}px` }}
							>
								{time.label}
							</div>
						))}
					</div>
					<div
						ref={contentRef}
						onScroll={handleScroll}
						className={cn("overflow-auto", contentClassName)}
					>
						<div
							className="relative"
							style={{ minWidth: `${stages.length * stageColumnWidth}px` }}
						>
						<div
							className="inline-grid"
							style={{
								gridTemplateColumns: `repeat(${stages.length}, minmax(0, ${stageColumnWidth}px))`,
								minWidth: `${stages.length * stageColumnWidth}px`,
							}}
						>
							{stages.map(([stageName, sets]) => (
								<div key={stageName} className="relative border-r">
									{timeSlots.map((time, slotIndex) => (
										<div
											key={time.id}
											className={onCellClick ? "cursor-pointer hover:bg-primary/5" : ""}
											style={{ height: `${HOUR_HEIGHT}px` }}
											onClick={onCellClick ? () => onCellClick(stageName, time.hour) : undefined}
										/>
									))}
									{sets.map((set) => {
										let top;
										const setAbsoluteStart = set.startHour;
										let effectiveSetStart = setAbsoluteStart;

										if (
											scheduleIsOvernight &&
											scheduleEndHour < scheduleStartHour &&
											setAbsoluteStart < scheduleStartHour
										) {
											effectiveSetStart = setAbsoluteStart + 24;
										}

										if (inverseOrder) {
											const setAbsoluteEnd = set.startHour + set.duration;
											let effectiveSetEnd = setAbsoluteEnd;
											if (
												scheduleIsOvernight &&
												scheduleEndHour < scheduleStartHour &&
												setAbsoluteEnd < scheduleStartHour
											) {
												effectiveSetEnd = setAbsoluteEnd + 24;
											}

											// Universal inverse order positioning for all festivals
											const setEndHoursFromGridStart =
												effectiveSetEnd - scheduleStartHour;
											const hoursOfSetEndFromGridLogicalEnd =
												totalHoursInSchedule - setEndHoursFromGridStart;
											top = `${Math.max(0, hoursOfSetEndFromGridLogicalEnd * HOUR_HEIGHT) + topOffset}px`;
										} else {
											const hoursFromGridTop =
												effectiveSetStart - scheduleStartHour;
											top = `${hoursFromGridTop * HOUR_HEIGHT}px`;
										}

										const calculatedHeight =
											set.duration * hourHeight - (inverseOrder ? 4 : 0);

										// Enhanced short set handling with proportional dimensions to prevent overlapping
										let finalHeight: number;

										if (set.duration <= 10 / 60) {
											// ≤10 minutes
											finalHeight = Math.max(
												24,
												Math.round(set.duration * hourHeight),
											);
										} else if (set.duration <= 15 / 60) {
											// 11-15 minutes
											finalHeight = Math.round(set.duration * hourHeight);
										} else if (set.duration <= 20 / 60) {
											// 16-20 minutes
											finalHeight = Math.round(set.duration * hourHeight);
										} else if (set.duration <= 25 / 60) {
											// 21-25 minutes
											finalHeight = Math.round(set.duration * hourHeight);
										} else if (set.duration <= 30 / 60) {
											// 26-30 minutes
											finalHeight = Math.round(set.duration * hourHeight);
										} else {
											// 31+ minutes - normal
											finalHeight = Math.max(minSetHeight, calculatedHeight);
										}

										const height = `${finalHeight}px`;
										const statusInfo = getStatusInfo(set.status);
										const style = { top, height };

										if (renderItem) {
											return renderItem(set, statusInfo, style);
										}

										return defaultSetRenderer(set, statusInfo, style);
									})}
								</div>
							))}
						</div>
						{/* Current time indicator line */}
						{currentTimeTop !== null && (
							<div
								className="pointer-events-none absolute right-0 left-0 z-[5]"
								style={{ top: `${currentTimeTop}px` }}
							>
								<div className="relative">
									<div className="h-[2px] w-full bg-radix-iris-9" />
									<div className="absolute top-1/2 left-0 -translate-y-1/2 rounded bg-radix-iris-9 px-1.5 py-0.5 font-black text-white text-[8px] uppercase leading-none tracking-wide">
										NOW
									</div>
								</div>
							</div>
						)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});

CalendarTable.displayName = "CalendarTable";
